import { GridColDef } from '@mui/x-data-grid-premium';
import {
  dateColumn,
  inputColunm,
  selectColumn,
  selectRenderer,
  solutionsRenderer,
} from '../BulkEditTable/utils';
import { getOptionsFromTranslation } from '../../utils/utils';
import { TType } from '../../hooks/useTranslation';
import { defaultRenderer } from '../ProjectListTable/utils';
import { Stack } from '@mui/material';
import copy from '@ingka/ssr-icon/paths/copy';
import trashCan from '@ingka/ssr-icon/paths/trash-can';
import { establishmentTypes } from '../../containers/BulkCreate/utils';
import Combobox, { MenuItem } from '@ingka/combobox';
import { BusinessUnit } from '../../models/buniessUnitType';
import { getUnitsByCategory } from '../SiteTab/SiteInfoFields/SiteInfoSelects/utils';
import Button from '@ingka/button';
import Tooltip from '@ingka/tooltip';
import CDSComboBox from '../CDSComboBox';
import PartOfCrpComboBox from '../PartOfCrpComboBox';
import { ESTABLISHMENT_TYPE } from '../ProjectTypeSelect/utils';

export const returnValidationClassName = (params) => {
  return (
    params?.api?.state?.customParams?.validationTriggered &&
    (params?.value === '' || params?.value === null) &&
    'bulk-table-error'
  );
};

export const returnColumns: (
  onChange,
  onBuChange,
  t: TType,
  copyHandler,
  deleteHandler,
  buOptions: BusinessUnit[],
  countryList,
  userOptions,
  onAddSolutionsClicked,
) => GridColDef<any, any, any>[] = (
  onChange,
  onBuChange,
  t,
  copyHandler,
  deleteHandler,
  buOptions,
  countryList,
  userOptions,
  onAddSolutionsClicked,
) => [
  {
    field: 'actions',
    cellClassName: () => {
      return `overlow-auto`;
    },
    headerName: 'Actions',
    renderCell: (params) => (
      <Stack direction={'row'} spacing={2} alignItems={'center'}>
        <Tooltip
          tooltipText="Duplicate project"
          describedById="duplicate-tooltip"
          position="trailing"
        >
          <Button
            size={'xsmall'}
            iconOnly
            type={'tertiary'}
            onClick={() => copyHandler(params.id)}
            ssrIcon={copy}
          />
        </Tooltip>
        <Tooltip
          tooltipText="Delete project"
          describedById="delete-tooltip"
          position="trailing"
        >
          <Button
            size={'xsmall'}
            iconOnly
            type={'tertiary'}
            onClick={() => deleteHandler(params.id)}
            ssrIcon={trashCan}
          />
        </Tooltip>
      </Stack>
    ),
  },
  {
    field: 'establishmentType',
    headerName: 'Project Type',
    width: 300,
    display: 'flex',
    valueGetter: (value) =>
      establishmentTypes.find((type) => type.value === value).label,
    renderCell: defaultRenderer,
  },
  inputColunm(
    'projectName',
    'Project Name',
    onChange,
    false,
    220,
    null,
    null,
    true,
  ),
  {
    field: 'countryCode',
    headerName: 'Country',
    type: 'singleSelect',
    aggregable: false,
    display: 'flex',
    minWidth: 250,
    cellClassName: returnValidationClassName,
    renderCell: (params) => {
      if (!['NEW', 'LADA'].includes(params.row.establishmentType))
        return defaultRenderer({ ...params, value: countryList[params.value] });
      return selectRenderer(params, countryList, onChange, false);
    },
  },
  {
    field: 'businessUnitId',
    headerName: 'BU ID',
    width: 500,
    cellClassName: (params) => {
      return `overlow-auto ${returnValidationClassName(params)}`;
    },
    renderCell: (params) => {
      if (['NEW', 'LADA'].includes(params.row.establishmentType)) return '';
      return (
        <Combobox
          className="combobox-story"
          filtering
          style={{ width: '100%' }}
          labelId="label-id"
          listboxId="combobox-listbox"
          onListBoxChange={(v) => {
            onBuChange(params?.id, v);
          }}
          placeholder="Choose an item"
          value={params?.value}
        >
          {buOptions.map((bu) => (
            <MenuItem
              id={bu?.businessUnitId}
              title={bu?.businessUnitFullName}
            />
          ))}
        </Combobox>
      );
    },
  },
  {
    field: 'partOfCrpPlan',
    headerName: t('common.partOfCRPPlan'),
    display: 'flex',
    minWidth: 300,
    cellClassName: 'overflow-visible',
    renderCell: (params) => (
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%' }}>
        <PartOfCrpComboBox
          id={'partOfCrp-' + params.row.id}
          label=""
          maxDisplayItems={1}
          partOfCrp={params.row.partOfCrp}
          handleUpdate={(value) => onChange(params.row.id, 'partOfCrp', value)}
        />
      </div>
    ),
  },
  {
    field: 'unitCategory',
    headerName: 'Unit Category',
    type: 'singleSelect',
    aggregable: false,
    display: 'flex',
    cellClassName: returnValidationClassName,
    minWidth: 250,
    renderCell: (params) => {
      if (params.row?.establishmentType === 'LADA')
        return defaultRenderer({ ...params, value: t('unitCategories.CMP') });
      return selectRenderer(
        params,
        getOptionsFromTranslation(t, 'unitCategories'),
        onChange,
        false,
      );
    },
  },
  {
    field: 'unitType',
    headerName: 'Unit Type',
    type: 'singleSelect',
    aggregable: false,
    display: 'flex',
    cellClassName: returnValidationClassName,
    minWidth: 250,
    renderCell: (params) => {
      return selectRenderer(
        params,
        getUnitsByCategory(
          params?.row?.unitCategory,
          t('unitTypes'),
          params?.row?.establishmentType,
        ),
        onChange,
        false,
      );
    },
  },
  {
    field: 'solutions',
    headerName: 'Solution',
    aggregable: false,
    display: 'flex',
    minWidth: 250,
    renderCell: (params) => {
      if (
        ![
          ESTABLISHMENT_TYPE.IMPROVE,
          ESTABLISHMENT_TYPE.FORMAT_CHANGE,
        ].includes(params.row.establishmentType)
      )
        return '';
      return solutionsRenderer(params, onAddSolutionsClicked, t);
    },
  },
  {
    field: 'newUnitType',
    headerName: 'New Unit Type',
    type: 'singleSelect',
    aggregable: false,
    display: 'flex',
    minWidth: 250,
    cellClassName: returnValidationClassName,
    renderCell: (params) => {
      if (params.row?.establishmentType !== 'FORMAT_TRANSFORMATION') return '';
      return selectRenderer(
        params,
        getUnitsByCategory(params?.row?.unitCategory, t('unitTypes')),
        onChange,
        false,
      );
    },
  },
  selectColumn(
    'confidential',
    'Confidentiality',
    getOptionsFromTranslation(t, 'confidentialityLevels'),
    onChange,
    false,
    returnValidationClassName,
  ),
  selectColumn(
    'transactionType',
    'Transaction Type',
    getOptionsFromTranslation(t, 'transactionTypes'),
    onChange,
    false,
    returnValidationClassName,
  ),
  dateColumn(
    'targetProjectStartDate',
    'Project Start Date',
    onChange,
    false,
    true,
  ),
  dateColumn(
    'targetProjectEndDate',
    'Project Delivery Date',
    onChange,
    false,
    true,
  ),
  {
    field: 'countryProjectResponsible',
    headerName: 'Country Project Responsible',
    display: 'flex',
    minWidth: 500,
    renderCell: (params) => (
      <CDSComboBox
        additionalStyles={{ background: 'white' }}
        value={params.value}
        onChange={(value) =>
          onChange(params.row.id, 'countryProjectResponsible', value)
        }
        id={'countryProjectResponsible' + params.row.id}
        options={userOptions}
        error={
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          params?.api?.state?.customParams?.validationTriggered &&
          (params?.value === '' || params?.value === null)
        }
      />
    ),
  },
];
